import React, { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Link from "components/link";
import IconButton from "components/icon-button";
import { FormattedMessage, injectIntl } from "react-intl";
import Icon from "components/icon";
import { TableIconCell, CustomTableRow } from "components/table";
import PropTypes from "prop-types";
import { HeaderCell } from "components/table";
import { Restricted, permissions } from "components/auth";
import { DeleteActionWithModal } from "components/buttons";
import withStyles from "components/with-styles";
import styles from "./styles.css.js";
import ApiConnectedPaginatedTable from "../../../components/table/paginated-table/api-connected-paginated-table";
import { getEventsService } from "../../../services/eventServices";
import { deleteEventAction } from "../../../services/commonService";
import { isAfter } from "date-fns";

import eventStatusTypes from "../utils/event-status-types";
import { isDeletedEvent, isFutureEvent, EVENTS_TABLE_PER_PAGE_KEY } from "../utils";
import { RestrictedToAdmin, useAuthDataContext } from "../../../components/auth";
import LinkWithIcon from "../../../components/link/link-with-icon";
import Chip from "@material-ui/core/Chip";
import OptionalDate from "../../../components/optional-date/optional-date";
import { useCurrentCustomerContext } from "../../../components/current-customer-context";

const Header = ({ showIcons }) => (
  <TableRow>
    <HeaderCell>
      <FormattedMessage id="common.name" />
    </HeaderCell>
    {showIcons && <TableIconCell />}
  </TableRow>
);

Header.propTypes = {
  /**
   * Controls whether to show action icons in the header
   * @type {boolean}
   */
  showIcons: PropTypes.bool,

  /**
   * Additional CSS classes
   * @type {Object}
   */
  classes: PropTypes.object,
};

const RowComponent = ({ classes, intl, item, refetchData, user }) => {
  const { hasAdminPrivileges } = useAuthDataContext();
  const { currentCustomerId, deployments } = useCurrentCustomerContext();

  const deployment = deployments?.find(d => d._id === item.deployment_id);

  const detailsUrl = `/event/details/${item._id}/customer/${currentCustomerId}`;
  const hasRecordings = !!item.recording_ids?.length;

  const canDelete = hasAdminPrivileges || isAfter(new Date(item.event.start_date), new Date());

  const canEditEnd =
    item && isAfter(new Date(item.event.end_date), new Date()) && item.event_status !== eventStatusTypes.FINISHED;
  const isDeleted = item?.event_status === eventStatusTypes.DELETED;

  return (
    <CustomTableRow
      key={item._id}
      href={detailsUrl}
      className={cn(classes.row, {
        [classes.alert]: item.event_status === eventStatusTypes.STARTED,
        [classes.future]: isFutureEvent(item.event_status),
        [classes.deleted]: isDeletedEvent(item.event_status),
      })}
    >
      <TableCell>
        <div className={classes.cell}>
          <div>
            <div className={classes.title}>{item.event.title}</div>
            {item.event.description !== item.event.title && <div>{item.event.description}</div>}
            <RestrictedToAdmin>
              <div className={classes.deployment}>
                {deployment && (
                  <LinkWithIcon
                    className={classes.link}
                    text={deployment?.codename}
                    iconName="settings-input-antenna"
                    color="primary"
                    disabled
                    href={`/deployment/details/${deployment._id}`}
                  />
                )}
              </div>
            </RestrictedToAdmin>
          </div>
          <div>
            <div className={cn(classes.status)}>
              {item.event_status ? (
                <Chip
                  classes={{
                    outlined: cn({
                      [classes.status__alert]: item.event_status === eventStatusTypes.STARTED,
                      [classes.status__future]: isFutureEvent(item.event_status),
                      [classes.status__deleted]: isDeletedEvent(item.event_status),
                    }),
                  }}
                  className={cn(classes.status)}
                  variant="outlined"
                  label={intl.formatMessage({ id: `event.eventStatus.in.${item.event_status}` })}
                />
              ) : null}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell colSpan={2}>
        <div>
          <OptionalDate
            label={intl.formatMessage({ id: "event.startDate" })}
            className={classes.date}
            icon="event-available"
            value={item.event.start_date * 1000}
          />
          <OptionalDate
            label={intl.formatMessage({ id: "event.endDate" })}
            className={classes.date}
            icon="event-available"
            value={item.event.end_date * 1000}
          />
        </div>
      </TableCell>
      <TableIconCell>
        <div className={classes.actions}>
          <Link href={detailsUrl} target="_blank" rel="noopener">
            <IconButton>
              <Icon name="launch" />
            </IconButton>
          </Link>
          {!isDeleted && (
            <>
              {(hasAdminPrivileges || canEditEnd) && (
                <Link href={`/event/edit/${item._id}/customer/${currentCustomerId}`}>
                  <IconButton>
                    <Icon name="edit" />
                  </IconButton>
                </Link>
              )}
              <Restricted permissions={[permissions.event_manage]}>
                {canDelete && (
                  <DeleteActionWithModal
                    disabledTooltipText={intl.formatMessage({ id: "app.event.delete.hasRecordings" })}
                    disabled={hasRecordings}
                    mutation={deleteEventAction}
                    mutationVariables={{ eventId: item._id, username: user?.sub }}
                    elementNameInResult="deleteEvent"
                    elementToDelete={item.title}
                    refetchDataOnSuccess
                    refetchData={refetchData}
                    item={item}
                  />
                )}
              </Restricted>
            </>
          )}
        </div>
      </TableIconCell>
    </CustomTableRow>
  );
};

RowComponent.propTypes = {
  /**
   * The event item to be displayed in the table row
   * @type {Object}
   * @property {string} _id - The unique identifier of the event
   * @property {Object} event - The event details
   * @property {string} event.title - The title of the event
   * @property {string} event.description - The description of the event
   * @property {string} event.start_date - The start date of the event
   * @property {string} event.end_date - The end date of the event
   * @property {string} event_status - The current status of the event
   * @property {string} deployment_id - The ID of the deployment
   * @property {Array} recording_ids - Array of recording IDs associated with the event
   */
  item: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    event: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      start_date: PropTypes.string.isRequired,
      end_date: PropTypes.string.isRequired,
    }).isRequired,
    event_status: PropTypes.string,
    deployment_id: PropTypes.string,
    recording_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,

  /**
   * Function to refetch data
   * @type {Function}
   */
  refetchData: PropTypes.func.isRequired,

  /**
   * React Intl object for internationalization
   * @type {Object}
   */
  intl: PropTypes.object.isRequired,

  /**
   * CSS classes object
   * @type {Object}
   */
  classes: PropTypes.object.isRequired,

  /**
   * User object containing user information
   * @type {Object}
   * @property {string} sub - User identifier
   */
  user: PropTypes.shape({
    sub: PropTypes.string,
  }),

  /**
   * Controls whether to show action icons
   * @type {boolean}
   */
  showIcons: PropTypes.bool,

  /**
   * ID of the currently selected row
   * @type {string}
   */
  selectedRowId: PropTypes.string,

  /**
   * Click handler for the row
   * @type {Function}
   * @param {Object} event - The click event
   * @param {string} id - The ID of the clicked row
   */
  onClick: PropTypes.func,
};

const StyledRowComponent = injectIntl(withStyles(styles)(RowComponent));

const EventPaginatedTable = ({ variables, noDataMessage = "event.tabs.events.no_data" }) => {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(() => {
    const savedPerPage = localStorage.getItem(EVENTS_TABLE_PER_PAGE_KEY);
    return savedPerPage ? parseInt(savedPerPage, 10) : 5;
  });

  const handleSetPage = value => {
    setPage(value);
  };

  const handleSetPerPage = value => {
    setPerPage(value);
    localStorage.setItem(EVENTS_TABLE_PER_PAGE_KEY, value.toString());
  };

  const [elements, setElements] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(null);

  const callback = response => {
    setLoading(false);
    setElements(response.data);
    setTotal(parseInt(response.headers["pagination-total-results"]));
  };

  const errorCallback = value => {
    setLoading(false);
    setError(value);
  };

  const refetchData = useCallback(
    () => {
      setLoading(true);
      const params = { page, perPage, limit: perPage, ...variables };
      getEventsService(params, callback, errorCallback);
    },
    [page, perPage, variables],
  );

  useEffect(
    () => {
      refetchData();
    },
    [refetchData],
  );

  return (
    <ApiConnectedPaginatedTable
      APIPetition={getEventsService}
      headerComponent={Header}
      rowComponent={StyledRowComponent}
      rowProps={{ refetchData }}
      paginatedElementsHandler={data => data}
      page={page}
      onChangePage={handleSetPage}
      onChangePerPage={handleSetPerPage}
      elements={elements}
      loading={loading}
      error={error}
      total={total}
      perPage={perPage}
      noDataMessage={noDataMessage}
    />
  );
};

EventPaginatedTable.propTypes = {
  /**
   * Object containing filter variables for the query
   * @type {Object}
   * @property {string} [search] - Search query string
   * @property {string} [status] - Filter by event status
   * @property {string} [deploymentId] - Filter by deployment ID
   * @property {string} [startDate] - Filter by start date
   * @property {string} [endDate] - Filter by end date
   */
  variables: PropTypes.shape({
    search: PropTypes.string,
    status: PropTypes.string,
    deploymentId: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),

  /**
   * Controls whether to show deleted events
   * @type {boolean}
   */
  showDeletedEvents: PropTypes.bool,

  /**
   * Enables debouncing for table query reloads
   * @type {boolean}
   */
  debounce: PropTypes.bool,

  /**
   * Controls whether to show action icons
   * @type {boolean}
   */
  showIcons: PropTypes.bool,

  /**
   * React Intl object for internationalization
   * @type {Object}
   */
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectIntl(EventPaginatedTable);
